import React from 'react';
import Select, { StylesConfig, SingleValue } from 'react-select';
import { Plus, X } from 'lucide-react';
import BasketSelect from './BasketSelect';
import type { FilterCondition, FilterFieldOption, FilterFieldType } from '../utils/employeeAdvancedFilters';
import { getFieldTypeFromList } from '../utils/employeeAdvancedFilters';

type FieldSelectOption = { value: string; label: string };

interface EmployeeAdvancedFilterPanelProps {
  filters: FilterCondition[];
  setFilters: React.Dispatch<React.SetStateAction<FilterCondition[]>>;
  filterFields: FilterFieldOption[];
  relatedDataLoading?: boolean;
  /** When true, filters are shown as read-only text (no editing). */
  readOnly?: boolean;
  /**
   * Field keys excluded from the field dropdown on other rows once that field is already used
   * (e.g. Status and Main location only appear once each).
   */
  excludeFieldValues?: string[];
  /** When set, "Clear all" calls this instead of clearing every row. */
  onClearFiltersOverride?: () => void;
  /** Rows whose field cannot be removed (delete button hidden; spacer keeps layout). */
  nonDeletableFieldValues?: string[];
  /** Rows whose field dropdown is disabled (cannot switch away from this field). */
  disableFieldSelectForFieldValues?: string[];
}

function formatFilterValueSummary(
  filter: FilterCondition,
  fieldType: FilterFieldType,
  fieldOptions: Array<{ value: string | number; label: string }>
): string {
  if (fieldType === 'dateRange' || fieldType === 'numberRange') {
    const from = filter.valueFrom != null && filter.valueFrom !== '' ? String(filter.valueFrom) : '—';
    const to = filter.valueTo != null && filter.valueTo !== '' ? String(filter.valueTo) : '—';
    return `${from} → ${to}`;
  }
  if (fieldType === 'boolean') {
    return filter.value === true ? 'Yes' : filter.value === false ? 'No' : '—';
  }
  if (fieldType === 'multiSelect' && Array.isArray(filter.value)) {
    const ids = filter.value as (string | number)[];
    const labels = ids.map((id) => {
      const opt = fieldOptions.find((o) => String(o.value) === String(id));
      return opt?.label ?? String(id);
    });
    return labels.length ? labels.join(', ') : '—';
  }
  if (fieldType === 'select' && fieldOptions.length > 0) {
    const opt = fieldOptions.find((o) => String(o.value) === String(filter.value));
    return opt?.label ?? (filter.value !== '' && filter.value != null ? String(filter.value) : '—');
  }
  return filter.value !== '' && filter.value != null ? String(filter.value) : '—';
}

const EmployeeAdvancedFilterPanel: React.FC<EmployeeAdvancedFilterPanelProps> = ({
  filters,
  setFilters,
  filterFields,
  relatedDataLoading,
  readOnly = false,
  excludeFieldValues = [],
  onClearFiltersOverride,
  nonDeletableFieldValues = [],
  disableFieldSelectForFieldValues = [],
}) => {
  const handleAddFilter = () => {
    const newFilter: FilterCondition = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      field: '',
      value: '',
    };
    setFilters((prev) => [...prev, newFilter]);
  };

  const handleRemoveFilter = (id: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleFilterChange = (
    id: string,
    field: keyof FilterCondition,
    value: string | number | boolean | number[] | string[] | null | undefined
  ) => {
    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );
  };

  const handleFilterFieldChange = (id: string, fieldValue: string) => {
    setFilters((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, field: fieldValue, value: '', valueFrom: undefined, valueTo: undefined } : f
      )
    );
  };

  const handleClearFilters = () => {
    if (onClearFiltersOverride) {
      onClearFiltersOverride();
    } else {
      setFilters([]);
    }
  };

  const getFieldType = (fieldValue: string): FilterFieldType =>
    getFieldTypeFromList(filterFields, fieldValue);

  const getFieldOptions = (fieldValue: string) => {
    const field = filterFields.find((f) => f.value === fieldValue);
    return field?.options || [];
  };

  /** Above Employee Details modals (z-[100]–z-[102]) and MessageDialog (z-[110]). */
  const FIELD_SELECT_MENU_Z = 120;

  const fieldSelectStyles: StylesConfig<FieldSelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
      },
      minHeight: '38px',
      fontSize: '14px',
    }),
    menuPortal: (base) => ({ ...base, zIndex: FIELD_SELECT_MENU_Z }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    input: (provided) => ({
      ...provided,
      color: '#374151',
    }),
  };

  const exclude = new Set(excludeFieldValues);
  const nonDeletable = new Set(nonDeletableFieldValues);
  const disableFieldSelect = new Set(disableFieldSelectForFieldValues);

  const buildFieldSelectOptionsForRow = (filter: FilterCondition): FieldSelectOption[] =>
    filterFields
      .filter((field) => {
        if (exclude.has(field.value)) {
          if (filter.field === field.value) return true;
          return !filters.some((f) => f.id !== filter.id && f.field === field.value);
        }
        return true;
      })
      .map((field) => ({ value: field.value, label: field.label }));

  if (readOnly) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-neutral-800">Filter employees</h3>
        </div>
        {filters.length === 0 ? (
          <p className="text-sm text-neutral-500 py-2">No filter conditions in this set.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {filters.map((filter) => {
              const fieldType = getFieldType(filter.field);
              const fieldOptions = getFieldOptions(filter.field);
              const selectedField = filterFields.find((f) => f.value === filter.field);
              const fieldLabel = selectedField?.label ?? (filter.field || '(field not set)');
              const summary = formatFilterValueSummary(filter, fieldType, fieldOptions);
              return (
                <li
                  key={filter.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-neutral-100 last:border-0"
                >
                  <span className="font-medium text-neutral-800 min-w-[140px]">{fieldLabel}</span>
                  <span className="text-neutral-600 break-words">{summary}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-neutral-800">Filter employees</h3>
          {relatedDataLoading && (
            <span className="text-xs text-blue-600">Loading related data…</span>
          )}
        </div>
        <div className="flex gap-2">
          {filters.length > 0 && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs text-neutral-600 hover:text-neutral-800 px-2 py-1"
            >
              Clear all
            </button>
          )}
          <button
            type="button"
            onClick={handleAddFilter}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            <Plus className="h-3 w-3" />
            Add filter
          </button>
        </div>
      </div>

      {filters.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-4">
          No filters applied. Click &quot;Add filter&quot; to add conditions (same fields as Employee List advanced
          filters).
        </p>
      ) : (
        <div className="space-y-3">
          {filters.map((filter) => {
            const fieldType = getFieldType(filter.field);
            const fieldOptions = getFieldOptions(filter.field);
            const selectedField = filterFields.find((f) => f.value === filter.field);
            const rowFieldOptions = buildFieldSelectOptionsForRow(filter);

            const selectedFieldOption: FieldSelectOption | null = filter.field
              ? rowFieldOptions.find((opt) => opt.value === filter.field) || null
              : null;

            const fieldLocked = !!filter.field && disableFieldSelect.has(filter.field);
            const hideDelete = !!filter.field && nonDeletable.has(filter.field);

            return (
              <div key={filter.id} className="flex flex-col sm:flex-row gap-2 p-3 bg-neutral-50 rounded-md">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Field</label>
                  <Select<FieldSelectOption>
                    options={rowFieldOptions}
                    value={selectedFieldOption}
                    onChange={(option: SingleValue<FieldSelectOption>) => {
                      handleFilterFieldChange(filter.id, option ? option.value : '');
                    }}
                    placeholder="Select field…"
                    styles={fieldSelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    isSearchable
                    isClearable={!fieldLocked}
                    isDisabled={fieldLocked}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? `No fields found for "${inputValue}"` : 'No fields available'
                    }
                  />
                </div>

                <div className="flex-1">
                  {fieldType !== 'dateRange' && fieldType !== 'numberRange' && (
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Value</label>
                  )}
                  {fieldType === 'boolean' ? (
                    <select
                      value={String(filter.value || '')}
                      onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value === 'true')}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select…</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : fieldType === 'dateRange' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">From</label>
                        <input
                          type="date"
                          value={String(filter.valueFrom || '')}
                          onChange={(e) => handleFilterChange(filter.id, 'valueFrom', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">To</label>
                        <input
                          type="date"
                          value={String(filter.valueTo || '')}
                          onChange={(e) => handleFilterChange(filter.id, 'valueTo', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  ) : fieldType === 'numberRange' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">From</label>
                        <input
                          type="number"
                          step="0.01"
                          value={filter.valueFrom !== null && filter.valueFrom !== undefined ? filter.valueFrom : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleFilterChange(filter.id, 'valueFrom', val === '' ? null : parseFloat(val));
                          }}
                          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">To</label>
                        <input
                          type="number"
                          step="0.01"
                          value={filter.valueTo !== null && filter.valueTo !== undefined ? filter.valueTo : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleFilterChange(filter.id, 'valueTo', val === '' ? null : parseFloat(val));
                          }}
                          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  ) : fieldType === 'multiSelect' && fieldOptions.length > 0 ? (
                    <BasketSelect
                      label=""
                      availableItems={fieldOptions.map((opt) => ({ id: opt.value, label: opt.label }))}
                      selectedIds={(() => {
                        if (Array.isArray(filter.value)) {
                          const arr = filter.value as (string | number | boolean)[];
                          return arr.filter((v): v is string | number => typeof v === 'string' || typeof v === 'number');
                        }
                        if (filter.value && (typeof filter.value === 'string' || typeof filter.value === 'number')) {
                          return [filter.value];
                        }
                        return [];
                      })()}
                      onChange={(selectedIds) => {
                        const isNumeric = fieldOptions.length > 0 && typeof fieldOptions[0].value === 'number';

                        if (isNumeric) {
                          const numValues: number[] = selectedIds.map((id) => {
                            const option = fieldOptions.find((opt) => String(opt.value) === String(id));
                            return option && typeof option.value === 'number' ? option.value : Number(id);
                          });
                          handleFilterChange(filter.id, 'value', numValues);
                        } else {
                          const strValues: string[] = selectedIds.map((id) => {
                            const option = fieldOptions.find((opt) => String(opt.value) === String(id));
                            return option ? String(option.value) : String(id);
                          });
                          handleFilterChange(filter.id, 'value', strValues);
                        }
                      }}
                      placeholder={`Select ${selectedField?.label.toLowerCase() || 'options'}…`}
                    />
                  ) : fieldType === 'select' && fieldOptions.length > 0 ? (
                    (() => {
                      /** Empty string is a real choice (e.g. Main location → All locations); do not add a second <option value=""> placeholder. */
                      const hasEmptyStringOption = fieldOptions.some(
                        (o) => o.value === '' || o.value === null || o.value === undefined
                      );
                      const selectValue =
                        filter.value === null || filter.value === undefined || filter.value === ''
                          ? ''
                          : String(filter.value);
                      return (
                    <select
                      value={selectValue}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        if (!selectedValue) {
                          handleFilterChange(filter.id, 'value', '');
                          return;
                        }
                        const selectedOption = fieldOptions.find((opt) => String(opt.value) === selectedValue);
                        if (selectedOption) {
                          handleFilterChange(filter.id, 'value', selectedOption.value);
                        } else {
                          const numValue = Number(selectedValue);
                          handleFilterChange(filter.id, 'value', isNaN(numValue) ? selectedValue : numValue);
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {!hasEmptyStringOption ? <option value="">Select…</option> : null}
                      {fieldOptions.map((option) => (
                        <option
                          key={option.value === '' || option.value === null ? `${filter.id}-empty` : String(option.value)}
                          value={String(option.value ?? '')}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                      );
                    })()
                  ) : (
                    <input
                      type="text"
                      value={String(filter.value || '')}
                      onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                      placeholder={`Enter ${selectedField?.label.toLowerCase() || 'value'}…`}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                <div className="flex items-end shrink-0">
                  {hideDelete ? (
                    <div className="w-10 h-10 shrink-0" aria-hidden />
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRemoveFilter(filter.id)}
                      className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Remove filter"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EmployeeAdvancedFilterPanel;
